import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { Calendar, Clock3, Images, LogIn, MapPin, PlayCircle, Shield, UserPlus, Users } from 'lucide-react';
import Header from '../../components/header/Header';
import Footer from '../../components/footer/Footer';
import LoginModal from '../../components/loginSection/LoginModal';
import { eventsService } from '../../services';
import { useUser } from '../../UserContext';
import './PublicEventPage.css';

const formatDate = (value) => {
    if (!value) {
        return 'Date to be confirmed';
    }

    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) {
        return 'Date to be confirmed';
    }

    return parsed.toLocaleDateString('en-GB', {
        day: '2-digit',
        month: 'short',
        year: 'numeric'
    });
};

const formatTime = (value) => {
    if (!value) {
        return 'Time to be confirmed';
    }

    return value.length === 5 ? value : value.slice(0, 5);
};

const PublicEventPage = () => {
    const { token } = useParams();
    const navigate = useNavigate();
    const { initialized, isAuthenticated } = useUser();
    const [isLoginModalOpen, setIsLoginModalOpen] = useState(false);
    const [eventData, setEventData] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [isResolving, setIsResolving] = useState(false);
    const [error, setError] = useState('');

    useEffect(() => {
        const loadPublicEvent = async () => {
            setIsLoading(true);
            setError('');

            try {
                const data = await eventsService.getSharedEvent(token);
                setEventData(data);
            } catch (loadError) {
                setError(loadError.message || 'We could not load this shared event right now.');
                setEventData(null);
            } finally {
                setIsLoading(false);
            }
        };

        if (token) {
            loadPublicEvent();
        }
    }, [token]);

    useEffect(() => {
        const resolveAndRedirect = async () => {
            if (!initialized || !isAuthenticated || !token) {
                return;
            }

            setIsResolving(true);
            try {
                const resolution = await eventsService.resolveSharedEvent(token);
                if (resolution?.dashboardUrl) {
                    navigate(resolution.dashboardUrl, { replace: true });
                }
            } catch (resolveError) {
                console.debug('Shared event redirect skipped:', resolveError?.message || resolveError);
            } finally {
                setIsResolving(false);
            }
        };

        resolveAndRedirect();
    }, [initialized, isAuthenticated, navigate, token]);

    useEffect(() => {
        if (eventData?.name) {
            document.title = `${eventData.name} | Shoot Connect`;
            return;
        }

        document.title = 'Shared Event | Shoot Connect';
    }, [eventData?.name]);

    const heroImage = useMemo(() => eventData?.images?.[0]?.imageUrl || null, [eventData?.images]);

    return (
        <div className="public-event-page">
            <Header
                openLoginModal={() => setIsLoginModalOpen(true)}
                isAuthenticated={initialized ? isAuthenticated : null}
                alwaysSolid
            />

            <main className="public-event-main">
                <div className="container">
                    <div className="public-event-back-row">
                        <Link to="/" className="public-event-back-link">{'<-'} Back to Shoot Connect</Link>
                        {eventData?.publicVenueUrl ? (
                            <Link to={eventData.publicVenueUrl} className="public-event-venue-link">
                                View venue profile
                            </Link>
                        ) : null}
                    </div>

                    {isLoading && (
                        <div className="public-event-state">
                            <h2>Loading shared event...</h2>
                        </div>
                    )}

                    {!isLoading && error && (
                        <div className="public-event-state error">
                            <h2>We could not load this shared event</h2>
                            <p>{error}</p>
                        </div>
                    )}

                    {!isLoading && !error && eventData && (
                        <>
                            <section className="public-event-hero">
                                <div className="public-event-hero-copy">
                                    <span className="public-event-pill">Shared Event</span>
                                    <h1>{eventData.name}</h1>
                                    <p>
                                        {eventData.description || 'A shared Shoot Connect event with venue details, gallery highlights, and who was shooting.'}
                                    </p>
                                    <div className="public-event-meta">
                                        <span><Calendar size={16} /> {formatDate(eventData.eventDate)}</span>
                                        <span><Clock3 size={16} /> {formatTime(eventData.eventTime)}</span>
                                        <span><MapPin size={16} /> {eventData.club?.name || 'Venue to be confirmed'}</span>
                                    </div>
                                    <div className="public-event-hero-actions">
                                        <button className="public-event-primary-btn" onClick={() => setIsLoginModalOpen(true)}>
                                            <LogIn size={16} />
                                            Log In
                                        </button>
                                        <Link to="/register" className="public-event-secondary-btn">
                                            <UserPlus size={16} />
                                            Sign Up
                                        </Link>
                                        {eventData.publicVenueUrl ? (
                                            <Link to={eventData.publicVenueUrl} className="public-event-ghost-btn">
                                                View Venue
                                            </Link>
                                        ) : null}
                                    </div>
                                    <div className="public-event-note">
                                        <Shield size={16} />
                                        Shared publicly without exposing private member details.
                                    </div>
                                    {isResolving && (
                                        <p className="public-event-redirecting">Opening this event in your dashboard...</p>
                                    )}
                                </div>
                                <div className="public-event-hero-media">
                                    {heroImage ? (
                                        <img src={heroImage} alt={`${eventData.name} gallery`} />
                                    ) : (
                                        <div className="public-event-hero-placeholder">
                                            <Images size={32} />
                                            <span>No hero image yet</span>
                                        </div>
                                    )}
                                </div>
                            </section>

                            <section className="public-event-stats">
                                <article><strong>{eventData.imageCount || 0}</strong><span>Photos</span></article>
                                <article><strong>{eventData.videoCount || 0}</strong><span>Videos</span></article>
                                <article><strong>{eventData.shooters?.length || 0}</strong><span>Shooters</span></article>
                                <article><strong>{eventData.eventType || 'Event'}</strong><span>Discipline</span></article>
                            </section>

                            <div className="public-event-layout">
                                <div className="public-event-main-column">
                                    <section className="public-event-card">
                                        <div className="public-event-card-header">
                                            <h3>Event Gallery</h3>
                                            <span>{eventData.images?.length || 0} photo{eventData.images?.length === 1 ? '' : 's'}</span>
                                        </div>
                                        {eventData.images?.length ? (
                                            <div className="public-event-gallery-grid">
                                                {eventData.images.map((image) => (
                                                    <img
                                                        key={image.eventImageId}
                                                        src={image.imageUrl}
                                                        alt={image.originalFileName || eventData.name}
                                                    />
                                                ))}
                                            </div>
                                        ) : (
                                            <p className="public-event-muted">No public photos have been added to this event yet.</p>
                                        )}
                                    </section>

                                    <section className="public-event-card">
                                        <div className="public-event-card-header">
                                            <h3>Event Videos</h3>
                                            <span>{eventData.videos?.length || 0} link{eventData.videos?.length === 1 ? '' : 's'}</span>
                                        </div>
                                        {eventData.videos?.length ? (
                                            <div className="public-event-video-list">
                                                {eventData.videos.map((video) => (
                                                    <a
                                                        key={video.eventVideoId}
                                                        className="public-event-video-card"
                                                        href={video.videoUrl}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                    >
                                                        <div>
                                                            <strong>{video.description || 'Event video'}</strong>
                                                            <span>{video.videoUrl}</span>
                                                        </div>
                                                        <PlayCircle size={20} />
                                                    </a>
                                                ))}
                                            </div>
                                        ) : (
                                            <p className="public-event-muted">No public videos have been added to this event yet.</p>
                                        )}
                                    </section>
                                </div>

                                <aside className="public-event-side-column">
                                    <section className="public-event-card">
                                        <div className="public-event-card-header">
                                            <h3>Venue</h3>
                                        </div>
                                        <strong>{eventData.club?.name || 'Venue to be confirmed'}</strong>
                                        <p className="public-event-muted">{eventData.club?.address || 'Venue address not available.'}</p>
                                        {eventData.publicVenueUrl ? (
                                            <Link to={eventData.publicVenueUrl} className="public-event-inline-link">
                                                Open public venue page
                                            </Link>
                                        ) : null}
                                    </section>

                                    <section className="public-event-card">
                                        <div className="public-event-card-header">
                                            <h3>Who Was Shooting</h3>
                                            <span><Users size={14} /> {eventData.shooters?.length || 0}</span>
                                        </div>
                                        {eventData.shooters?.length ? (
                                            <div className="public-event-shooter-list">
                                                {eventData.shooters.map((shooter) => (
                                                    <span key={shooter} className="public-event-shooter-chip">{shooter}</span>
                                                ))}
                                            </div>
                                        ) : (
                                            <p className="public-event-muted">No shooters have been listed publicly for this event.</p>
                                        )}
                                    </section>

                                    <section className="public-event-card public-event-cta-card">
                                        <h3>Track events with Shoot Connect</h3>
                                        <p>Join to create your own events, score shoots, manage galleries, and connect with your venue.</p>
                                        <div className="public-event-cta-stack">
                                            <button className="public-event-primary-btn" onClick={() => setIsLoginModalOpen(true)}>
                                                <LogIn size={16} />
                                                Log In
                                            </button>
                                            <Link to="/register" className="public-event-secondary-btn">
                                                <UserPlus size={16} />
                                                Create Free Account
                                            </Link>
                                        </div>
                                    </section>
                                </aside>
                            </div>
                        </>
                    )}
                </div>
            </main>

            <LoginModal isOpen={isLoginModalOpen} onClose={() => setIsLoginModalOpen(false)} />
            <Footer />
        </div>
    );
};

export default PublicEventPage;
